import React, { useState } from 'react';
import { Eye, Loader2, Pencil, Send, Trash2, FileSignature } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Checkbox } from '../ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { prospects } from '../../api';
import { DOC_KEYS, errMsg } from './constants';

const BADGE = { draft: 'bg-stone-100 text-stone-700', sent: 'bg-amber-50 text-amber-800', signed: 'bg-emerald-50 text-emerald-800' };

const EditDoc = ({ doc, onClose, onSave }) => {
  const [d, setD] = useState({ title: doc.title, body: doc.body });
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="doc-edit-dialog">
        <DialogHeader><DialogTitle>Edit document for this prospect</DialogTitle></DialogHeader>
        <Input value={d.title} onChange={(e) => setD({ ...d, title: e.target.value })} className="h-9 font-semibold" data-testid="doc-edit-title"/>
        <Textarea value={d.body} onChange={(e) => setD({ ...d, body: e.target.value })} rows={20} className="font-mono text-xs" data-testid="doc-edit-body"/>
        <p className="text-xs text-stone-500">Merge fields like {'{{monthly_fee}}'} fill in automatically. Changes here only apply to this prospect.</p>
        <DialogFooter><Button variant="outline" onClick={onClose}>Cancel</Button><Button onClick={() => onSave(d)} className="bg-emerald-800 hover:bg-emerald-900 text-white" data-testid="doc-edit-save">Save</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export const DocumentsPanel = ({ p, onChange, toast }) => {
  const docs = p.documents || [];
  const [pick, setPick] = useState(() => (docs.length ? docs.map((d) => d.key) : DOC_KEYS.map(([k]) => k)));
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState(null);
  const run = (fn, ok) => { setBusy(true); return fn().then((np) => { onChange(np); ok && toast({ title: ok }); }).catch((e) => toast({ title: 'Failed', description: errMsg(e), variant: 'destructive' })).finally(() => setBusy(false)); };
  const drafts = docs.filter((d) => d.status === 'draft').length;

  return (
    <div className="space-y-5" data-testid="documents-panel">
      <div className="rounded-xl bg-white border border-stone-200 p-5">
        <div className="font-semibold text-stone-900 flex items-center gap-2"><FileSignature size={16} className="text-emerald-800"/> Prepare engagement documents</div>
        <p className="text-xs text-stone-500 mt-0.5">Pick the documents to send. They're auto-filled from your firm templates with this prospect's details, services and fees.</p>
        <div className="mt-4 grid sm:grid-cols-2 gap-2">
          {DOC_KEYS.map(([k, l]) => (
            <label key={k} className="flex items-center gap-2.5 rounded-lg border border-stone-200 px-3 py-2.5 text-sm cursor-pointer hover:bg-stone-50">
              <Checkbox checked={pick.includes(k)} onCheckedChange={(c) => setPick(c ? [...pick, k] : pick.filter((x) => x !== k))} data-testid={`doc-pick-${k}`}/> {l}
            </label>
          ))}
        </div>
        <div className="mt-4 flex flex-wrap gap-2">
          <Button variant="outline" disabled={busy || !pick.length} onClick={() => run(() => prospects.prepare(p.id, pick), 'Documents prepared')} data-testid="docs-prepare-btn">
            {busy && <Loader2 size={14} className="mr-2 animate-spin"/>}{docs.length ? 'Update document set' : 'Prepare documents'}
          </Button>
          <Button disabled={busy || !drafts} onClick={() => run(() => prospects.send(p.id), 'Sent for signature — share the onboarding link')} className="bg-emerald-800 hover:bg-emerald-900 text-white" data-testid="docs-send-btn">
            <Send size={14} className="mr-2"/> Send {drafts || ''} for signing
          </Button>
        </div>
      </div>

      <div className="rounded-xl bg-white border border-stone-200 overflow-hidden">
        <div className="px-5 py-4 border-b border-stone-100 font-semibold text-stone-900">Documents ({docs.filter((d) => d.status === 'signed').length}/{docs.length} signed)</div>
        {!docs.length && <div className="p-8 text-center text-sm text-stone-500">No documents prepared yet.</div>}
        <div className="divide-y divide-stone-100">
          {docs.map((d) => (
            <div key={d.id} className="flex items-center gap-3 px-5 py-3" data-testid={`doc-row-${d.key}`}>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium text-stone-900">{d.title}</div>
                {d.signature && <div className="text-xs text-stone-500">Signed by {d.signature.name} · {d.signature.at} · IP {d.signature.ip}</div>}
              </div>
              <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full capitalize ${BADGE[d.status]}`} data-testid={`doc-status-${d.key}`}>{d.status === 'sent' ? 'awaiting signature' : d.status}</span>
              <a href={prospects.docPdfUrl(p.id, d.id)} target="_blank" rel="noreferrer" className="p-1.5 text-stone-500 hover:text-emerald-800" title={d.status === 'signed' ? 'Signed PDF' : 'Preview PDF'} data-testid={`doc-pdf-${d.key}`}><Eye size={16}/></a>
              {d.status !== 'signed' && <button onClick={() => setEditing(d)} className="p-1.5 text-stone-500 hover:text-emerald-800" title="Edit" data-testid={`doc-edit-${d.key}`}><Pencil size={15}/></button>}
              {d.status !== 'signed' && <button onClick={() => window.confirm(`Remove ${d.title}?`) && run(() => prospects.removeDoc(p.id, d.id), 'Document removed')} className="p-1.5 text-stone-400 hover:text-red-600" title="Remove" data-testid={`doc-remove-${d.key}`}><Trash2 size={15}/></button>}
            </div>
          ))}
        </div>
      </div>
      {editing && <EditDoc doc={editing} onClose={() => setEditing(null)} onSave={(d) => run(() => prospects.editDoc(p.id, editing.id, d), 'Document updated').then(() => setEditing(null))}/>}
    </div>
  );
};
